import UpgradeIcon from "@app/icons/upgrade";
import UpgradeService from "@app/services/http/upgrade.service";
import {
  Bank,
  CreatePaymentMethod,
  CreatePurchase,
  Purchase,
  Upgrade,
} from "@app/types";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useEffect, useState } from "react";
import StepOne from "./components/step-one.component";
import BankService from "@app/services/http/bank.service";
import StepTwo from "./components/step-two.component";
import StepThree from "./components/step-three.component";
import PurchaseService from "@app/services/http/purchase.service";
import { EWallet } from "@app/constants";
import { Environment } from "@environments/environment";
import { useSearchParams } from "react-router-dom";

export enum UpgradeStep {
  ONE = 1,
  TWO = 2,
  THREE = 3,
}

function UpgradePage() {
  const [step, setStep] = useState(UpgradeStep.ONE);
  const [upgrades, setUpgrades] = useState<Upgrade[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [bookId, setBookId] = useState<number>();
  const [purchase, setPurchase] = useState<Purchase>();
  const [packageIdSelected, setPackageIdSelected] = useState<number>();
  const [bankIdSelected, setBankIdSelected] = useState<number>();
  const [walletIdSelected, setWalletIdSelected] = useState<EWallet>();

  const [searchParams] = useSearchParams();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    const bookId = searchParams.get("book_id");

    if (bookId && !isNaN(Number(bookId))) {
      setBookId(Number(bookId));
      setStep(UpgradeStep.TWO);
    }
  }, [searchParams.get("book_id")]);

  useEffect(() => {
    Promise.all([
      UpgradeService.getUpgrade(abortController.signal),
      BankService.getBanks(abortController.signal),
    ]).then(([upgradeRes, bankRes]) => {
      setUpgrades(upgradeRes.data.items);
      setBanks(bankRes.data.items);
    });
  }, []);

  const handlePackageClick = (packageId: number) => {
    setPackageIdSelected(packageId);
  };

  const handleBankClick = (bankId?: number) => {
    setBankIdSelected(bankId);
  };

  const handleWalletClick = (walletId?: number) => {
    setWalletIdSelected(walletId);
  };

  const handleSetStep = (step: UpgradeStep) => {
    setStep(step);
  };

  const handleConfirm = () => {
    if (
      (!bankIdSelected && !walletIdSelected) ||
      (!packageIdSelected && !bookId)
    ) {
      return;
    }

    if (bankIdSelected) {
      const createPurchase: CreatePurchase = {
        bankId: bankIdSelected,
        serviceId: packageIdSelected,
        bookId,
      };

      PurchaseService.createPurchase(createPurchase).then((res) => {
        setPurchase(res.data);
        setStep(UpgradeStep.THREE);
      });
    } else if (walletIdSelected) {
      switch (walletIdSelected) {
        case EWallet.MOMO: {
          const createMomoPayment: CreatePaymentMethod = {
            redirectUrl: String(Environment.REDIRECT_PAYMENT_URL),
            serviceId: packageIdSelected,
            bookId,
          };

          PurchaseService.createMomoPayment(createMomoPayment).then((res) => {
            window.location.replace(res.data.payUrl);
          });
          break;
        }
        case EWallet.ZALO_PAY: {
          const createZaloPayment: CreatePaymentMethod = {
            redirectUrl: String(Environment.REDIRECT_PAYMENT_URL),
            serviceId: packageIdSelected,
            bookId,
          };

          PurchaseService.createZaloPayment(createZaloPayment).then((res) => {
            window.location.replace(res.data.order_url);
          });
          break;
        }
      }
    }
  };

  const renderStep = () => {
    switch (step) {
      case UpgradeStep.ONE:
        return (
          <StepOne
            upgrades={upgrades}
            handlePackageClick={handlePackageClick}
            packageIdSelected={packageIdSelected}
            handleSetStep={handleSetStep}
          />
        );
      case UpgradeStep.TWO:
        return (
          <StepTwo
            banks={banks}
            handleBankClick={handleBankClick}
            bankIdSelected={bankIdSelected}
            handleConfirm={handleConfirm}
            handleWalletClick={handleWalletClick}
            walletIdSelected={walletIdSelected}
          />
        );
      case UpgradeStep.THREE:
        return <StepThree purchase={purchase} handleSetStep={handleSetStep} />;
    }
  };

  return (
    <div className="mt-20 font-[SVN-Gilroy] min-h-[calc(100vh-348px)]">
      <div className="flex justify-center items-end gap-3">
        <div className="w-[186px]">
          <img className="w-full" src={Images.LogoText.default} alt="" />
        </div>
        <div className="text-sm w-[116px] h-9 bg-gradient-to-b from-[#FC8B22] to-[#FFDD2D] flex justify-center items-center gap-1 rounded-xl text-white font-semibold">
          <UpgradeIcon color="white" />
          <div>PREMIUM</div>
        </div>
      </div>
      {renderStep()}
    </div>
  );
}

export default UpgradePage;
